import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { toastFlashMessage } from 'utils';

import {
  createModuleAssessment,
  getModuleAssessmentQuestions,
  putModuleAssessmentQuestions,
  getAIStatusPerModule,
  getSkillPathYourProgress,
  getSkillPathsResume,
  getSkillPathsHistory,
  getSkillPathsTotal,
  getUserTypeDetails,
  getSkillPathTotalUsersProgress,
  getSkillPathDetails,
  getHomeSkillPaths
} from './Training.services';
import { useModuleAssessmentsAIStore } from './assessment/ModuleBased/ObtainWithAI/ModuleAssessmentAI.store';

export const TRAINING_CACHE_KEYS = {
  MODULE_ASSESSMENT: 'training_module_assessment',
  MODULE_ASSESSMENT_QUESTIONS: 'training_module_assessment_questions',
  MODULE_ASSESSMENT_STATUS_AI: 'training_module_assessment_status_ai',
  SKILL_PATHS_YOUR_PROGRESS: 'skill_paths_your_progress',
  SKILL_PATHS_RESUME: 'skill_paths_resume',
  SKILL_PATH_HISTORY: 'skill_paths_history',
  SKILL_PATH_TOTAL: 'skill_path_total',
  SKILL_PATH_USER_TYPE_DETAILS: 'skill_path_user_type_details',
  SKILL_PATH_TOTAL_USER_PROGRESS: 'skill_path_total_user_progress',
  SKILL_PATH_DETAILS: 'skill_path_details',
  HOME_SKILL_PATHS: 'home_skill_paths',
};

export const useCreateModuleAssessment = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ module_uuid, questions }) => createModuleAssessment({ module_uuid, questions }),
    {
      onSuccess: (response) => {
        toastFlashMessage(response?.message, 'success');
        queryClient.invalidateQueries([TRAINING_CACHE_KEYS.MODULE_ASSESSMENT]);
      },
    }
  );
};

export const useGetModuleAssessmentQuestions = ({ module_uuid, enabled }) => {
  return useQuery({
    queryKey: [TRAINING_CACHE_KEYS.MODULE_ASSESSMENT_QUESTIONS, module_uuid],
    queryFn: () => getModuleAssessmentQuestions(module_uuid),
    enabled: enabled && Boolean(module_uuid),
  });
};

export const useUpdateModuleAssessmentQuestions = () => {
  return useMutation(
    ({ module_uuid, questions }) => putModuleAssessmentQuestions({ module_uuid, questions }),
    {
      onSuccess: (response) => {
        toastFlashMessage(response?.message, 'success');
      },
    }
  );
};

export const useGetAIStatusPerModule = ({ module_uuid, number_id, enabled }) => {
  const setModuleWithAI = useModuleAssessmentsAIStore((state) => state?.setModuleWithAI);
  return useQuery({
    queryKey: [TRAINING_CACHE_KEYS.MODULE_ASSESSMENT_STATUS_AI, module_uuid],
    queryFn: () => getAIStatusPerModule(module_uuid),
    enabled: enabled && Boolean(module_uuid),
    onSuccess: (response) => {
      const moduleWithAi = {
        module_uuid,
        with_ai: response?.data?.module_with_ai,
        number_id,
      };
      setModuleWithAI(moduleWithAi);
    },
  });
};

export const useGetSkillPathYourProgress = ({ enabled }) => {
  return useQuery({
    queryKey: [TRAINING_CACHE_KEYS.SKILL_PATHS_YOUR_PROGRESS],
    queryFn: () => getSkillPathYourProgress(),
    enabled: enabled,
  });
};

export const useGetSkillPathsResume = ({ enabled }) => {
  return useQuery({
    queryKey: [TRAINING_CACHE_KEYS.SKILL_PATHS_RESUME],
    queryFn: () => getSkillPathsResume(),
    enabled: enabled,
  });
};

export const useGetSkillPathsHistory = ({ enabled, page }) => {
  return useQuery({
    queryKey: [TRAINING_CACHE_KEYS.SKILL_PATH_HISTORY],
    queryFn: () => getSkillPathsHistory({ page }),
    enabled: enabled,
  });
};

export const useGetSkillPathsTotal = ({ enabled }) => {
  return useQuery({
    queryKey: [TRAINING_CACHE_KEYS.SKILL_PATH_TOTAL],
    queryFn: () => getSkillPathsTotal(),
    enabled: enabled,
  });
};

export const useGetUserTypeDetails = ({ enabled }) => {
  return useQuery({
    queryKey: [TRAINING_CACHE_KEYS.SKILL_PATH_USER_TYPE_DETAILS],
    queryFn: () => getUserTypeDetails(),
    enabled: enabled,
  });
};

export const useGetSkillPathTotalUsersProgress = ({ enabled }) => {
  return useQuery({
    queryKey: [TRAINING_CACHE_KEYS.SKILL_PATH_TOTAL_USER_PROGRESS],
    queryFn: () => getSkillPathTotalUsersProgress(),
    enabled: enabled,
  });
};

export const useGetSkillPathDetails = ({ enabled, page }) => {
  return useQuery({
    queryKey: [TRAINING_CACHE_KEYS.SKILL_PATH_DETAILS],
    queryFn: () => getSkillPathDetails({ page }),
    enabled: enabled,
  });
};

export const useGetHomeSkillPaths = ({ enabled }) => {
  return useQuery({
    queryKey: [TRAINING_CACHE_KEYS.HOME_SKILL_PATHS],
    queryFn: () => getHomeSkillPaths(),
    enabled: enabled,
  });
};
